/**
 * TesoreríaTab - Componente principal para gestión de tesorería
 * Orquesta: Badge de pendientes, Tabla de movimientos, Formulario de edición, Importador, Historial
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Button, Grid, TextField, FormControl, InputLabel, Select, MenuItem,
  Alert, Dialog, DialogContent, DialogTitle, DialogActions, Divider, Typography
} from '@mui/material';
import {
  Upload as UploadIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

// Componentes compartidos
import { CardContainer, LoadingState } from '../../components/shared';
import useNotification from '../../hooks/useNotification';

import tesoreriaService from '../../services/tesoreriaService';
import { catalogosService } from '../../services/catalogosService';
import { entidadesService } from '../../services/entidadesService';
import BadgePendientes from './BadgePendientes';
import MovimientosTab from './MovimientosTab';
import FormularioMovimiento from './FormularioMovimiento';
import ImportadorExcel from './ImportadorExcel';
import HistorialExportaciones from './HistorialExportaciones';
import HistorialImportacionesTesoreria from './HistorialImportacionesTesoreria';

const TesoreríaTab = () => {
  // Redux
  const { empresaActiva: empresa } = useSelector(state => state.empresa);
  const { showNotification } = useNotification();

  // Estados principales
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [mes, setMes] = useState(new Date().getMonth() + 1);

  // Estados para modal de exportación
  const [abrirModalExportacion, setAbrirModalExportacion] = useState(false);
  const [forzarExportacion, setForzarExportacion] = useState(false);
  const [cantidadParaExportar, setCantidadParaExportar] = useState(0);
  const [folioExportacion, setFolioExportacion] = useState('');

  // Datos cargados
  const [pendientes, setPendientes] = useState(null);
  const [movimientos, setMovimientos] = useState([]);
  const [exportaciones, setExportaciones] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [centros, setCentros] = useState([]);
  const [clasificadores, setClasificadores] = useState([]);
  const [entidades, setEntidades] = useState([]);

  // Estados de carga
  const [cargandoPendientes, setCargandoPendientes] = useState(false);
  const [cargandoMovimientos, setCargandoMovimientos] = useState(false);
  const [cargandoExportaciones, setCargandoExportaciones] = useState(false);
  const [cargandoCatalogos, setCargandoCatalogos] = useState(false);
  const [exportando, setExportando] = useState(false);

  // Estados de UI
  const [errorPendientes, setErrorPendientes] = useState('');
  const [errorMovimientos, setErrorMovimientos] = useState('');
  const [errorExportaciones, setErrorExportaciones] = useState('');

  // Diálogos y formulario
  const [abrirImportador, setAbrirImportador] = useState(false);
  const [movimientoSeleccionado, setMovimientoSeleccionado] = useState(null);
  const [guardandoMovimiento, setGuardandoMovimiento] = useState(false);

  // Cargar pendientes (badge)
  const cargarPendientes = useCallback(async (rutEmpresa, anioParam, mesParam) => {
    setCargandoPendientes(true);
    setErrorPendientes('');

    try {
      const data = await tesoreriaService.obtenerPendientes(rutEmpresa, anioParam, mesParam);
      setPendientes(data);
    } catch (error) {
      setErrorPendientes('Error al cargar información de pendientes');
      console.error(error);
    } finally {
      setCargandoPendientes(false);
    }
  }, []);

  // Cargar movimientos
  const cargarMovimientos = useCallback(async (rutEmpresa, anioParam, mesParam) => {
    setCargandoMovimientos(true);
    setErrorMovimientos('');

    try {
      const data = await tesoreriaService.cargarMovimientos(rutEmpresa, anioParam, mesParam);
      setMovimientos(data || []);
    } catch (error) {
      setErrorMovimientos('Error al cargar movimientos');
      console.error(error);
    } finally {
      setCargandoMovimientos(false);
    }
  }, []);

  // Cargar exportaciones
  const cargarExportaciones = useCallback(async (rutEmpresa, anioParam, mesParam) => {
    setCargandoExportaciones(true);
    setErrorExportaciones('');

    try {
      const data = await tesoreriaService.listarExportaciones(rutEmpresa, anioParam, mesParam);
      setExportaciones(data || []);
    } catch (error) {
      setErrorExportaciones('Error al cargar exportaciones');
      console.error(error);
    } finally {
      setCargandoExportaciones(false);
    }
  }, []);

  // Cargar catálogos (UNA SOLA VEZ cuando entra a la página)
  // Se cargan en paralelo DESPUÉS que los datos de tesorería terminen
  const cargarCatalogos = useCallback(async (rutEmpresa) => {
    if (!rutEmpresa || cuentas.length > 0) return; // Si ya se cargaron, no recargar

    setCargandoCatalogos(true);

    try {
      // Cargar todos los catálogos en paralelo
      const [cuentasData, centrosData, clasificadoresData, entidadesData] = await Promise.all([
        catalogosService.getCuentas(true, rutEmpresa),
        catalogosService.getCentros(true, rutEmpresa),
        catalogosService.getClasificadores(null, true, rutEmpresa),
        entidadesService.getEntidades(true)
      ]);

      setCuentas(cuentasData || []);
      setCentros(centrosData || []);
      setClasificadores(clasificadoresData || []);
      setEntidades(entidadesData || []);
    } catch (error) {
      console.error('Error al cargar catálogos:', error);
    } finally {
      setCargandoCatalogos(false);
    }
  }, [cuentas.length]);

  // Cargar datos de tesorería (movimientos, pendientes, exportaciones)
  const cargarDatosTesoreria = useCallback(async (rutEmpresa, anioParam, mesParam) => {
    if (!rutEmpresa) return;

    // Cargar en paralelo (máximo 3 conexiones)
    await Promise.all([
      cargarPendientes(rutEmpresa, anioParam, mesParam),
      cargarMovimientos(rutEmpresa, anioParam, mesParam),
      cargarExportaciones(rutEmpresa, anioParam, mesParam)
    ]);
  }, [cargarPendientes, cargarMovimientos, cargarExportaciones]);

  // useEffect 1: Cargar datos de tesorería cuando cambia período o empresa
  useEffect(() => {
    if (empresa) {
      const rutEmpresa = empresa.rut || empresa.rut_empresa;
      cargarDatosTesoreria(rutEmpresa, anio, mes);
    }
  }, [anio, mes, empresa, cargarDatosTesoreria]);

  // useEffect 2: Cargar catálogos cuando cambia empresa (independiente)
  useEffect(() => {
    if (empresa) {
      const rutEmpresa = empresa.rut || empresa.rut_empresa;
      cargarCatalogos(rutEmpresa);
    }
  }, [empresa, cargarCatalogos]);

  // Manejar selección de movimiento
  const handleMovimientoSeleccionado = (movimiento) => {
    setMovimientoSeleccionado(movimiento);
    // Los catálogos ya están precargados desde el useEffect
  };

  // Guardar cambios de movimiento
  const handleGuardarMovimiento = async (movimientoId, datosActualizar) => {
    if (!empresa) return;

    setGuardandoMovimiento(true);

    try {
      await tesoreriaService.actualizarMovimiento(movimientoId, datosActualizar);

      showNotification('Movimiento actualizado correctamente', 'success');
      setMovimientoSeleccionado(null);

      // Recargar datos
      const rutEmpresa = empresa.rut || empresa.rut_empresa;
      await Promise.all([
        cargarMovimientos(rutEmpresa, anio, mes),
        cargarPendientes(rutEmpresa, anio, mes)
      ]);
    } catch (error) {
      setErrorMovimientos(error.message || 'Error al guardar movimiento');
    } finally {
      setGuardandoMovimiento(false);
    }
  };

  // Cancelar edición
  const handleCancelarEdicion = () => {
    setMovimientoSeleccionado(null);
  };

  // Manejar importación exitosa
  const handleImportExitoso = async () => {
    if (!empresa) return;

    showNotification('Archivo importado exitosamente', 'success');
    setAbrirImportador(false);

    const rutEmpresa = empresa.rut || empresa.rut_empresa;
    await Promise.all([
      cargarMovimientos(rutEmpresa, anio, mes),
      cargarPendientes(rutEmpresa, anio, mes)
    ]);
  };

  // Abrir modal de exportación con contador
  const handleAbrirModalExportacion = async () => {
    if (!pendientes || !empresa) return;

    // Calcular cantidad a exportar
    const completadosNoExportados = pendientes.completados || 0;
    const exportados = movimientos.filter(m => m.estado === 'EXPORTADO').length;

    // Sin forzar: solo completados no exportados
    // Con forzar: completados no exportados + exportados
    const cantidad = forzarExportacion ? completadosNoExportados + exportados : completadosNoExportados;

    // Obtener folio actual del backend
    try {
      const rutEmpresa = empresa.rut || empresa.rut_empresa;
      const response = await tesoreriaService.obtenerFolioActual(rutEmpresa);
      setFolioExportacion(String(response.folio_actual || ''));
    } catch (error) {
      console.error('Error al obtener folio actual:', error);
      setFolioExportacion('');
    }

    setCantidadParaExportar(cantidad);
    setAbrirModalExportacion(true);
  };

  // Confirmar exportación desde el modal
  const handleConfirmarExportacion = async () => {
    if (!empresa) return;

    const rutEmpresa = empresa.rut || empresa.rut_empresa;
    setExportando(true);
    setErrorMovimientos('');
    setAbrirModalExportacion(false);

    try {
      const response = await tesoreriaService.exportarMovimientos(
        rutEmpresa,
        anio,
        mes,
        folioExportacion ? parseInt(folioExportacion) : null,
        !forzarExportacion, // solo_pendientes (false si forzar=true)
        forzarExportacion   // forzar
      );

      showNotification(
        `Exportación completada: ${response.cantidad_movimientos} movimientos, ` +
        `${response.cantidad_asientos} asientos`,
        'success'
      );

      // Resetear estado del modal
      setForzarExportacion(false);
      setCantidadParaExportar(0);
      setFolioExportacion('');

      // Descargar automáticamente
      if (response.exportacion_id) {
        try {
          const blob = await tesoreriaService.descargarExportacion(response.exportacion_id);
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          const timestamp = new Date().toISOString().replace(/[:.]/g, '').slice(0, 15);
          link.download = `tesoreria_${rutEmpresa}_${anio}${String(mes).padStart(2, '0')}_${timestamp}.csv`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } catch (downloadError) {
          console.error('Error al descargar archivo automáticamente:', downloadError);
          // No mostramos error al usuario porque la exportación ya fue exitosa
        }
      }

      // Recargar datos después de descargar
      setTimeout(async () => {
        await Promise.all([
          cargarMovimientos(rutEmpresa, anio, mes),
          cargarPendientes(rutEmpresa, anio, mes),
          cargarExportaciones(rutEmpresa, anio, mes)
        ]);
      }, 500);
    } catch (error) {
      setErrorMovimientos(error.response?.data?.detail || 'Error al exportar');
      console.error(error);
    } finally {
      setExportando(false);
    }
  };

  // Cerrar modal sin exportar
  const handleCerrarModalExportacion = () => {
    setAbrirModalExportacion(false);
    setForzarExportacion(false);
    setCantidadParaExportar(0);
    setFolioExportacion('');
  };

  // Refrescar datos
  const handleRefrescar = async () => {
    if (empresa) {
      const rutEmpresa = empresa.rut || empresa.rut_empresa;
      await cargarDatosTesoreria(rutEmpresa, anio, mes);
    }
  };

  if (!empresa) {
    return <LoadingState loading={true} loadingMessage="Cargando empresa..." />;
  }

  const rutEmpresa = empresa.rut || empresa.rut_empresa;

  return (
    <CardContainer
      title="Tesorería"
      subheader={`Gestión de movimientos de tesorería - ${empresa.nombre_corto || empresa.razon_social}`}
    >
      {/* Header con controles */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        {/* Año */}
        <TextField
          label="Año"
          type="number"
          value={anio}
          onChange={(e) => setAnio(parseInt(e.target.value))}
          inputProps={{ min: 2020, max: new Date().getFullYear() + 1 }}
          size="small"
          sx={{ width: '100px' }}
        />

        {/* Mes */}
        <FormControl size="small" sx={{ width: '120px' }}>
          <InputLabel>Mes</InputLabel>
          <Select
            value={mes}
            label="Mes"
            onChange={(e) => setMes(e.target.value)}
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
              <MenuItem key={m} value={m}>
                {new Date(2024, m - 1).toLocaleString('es-ES', { month: 'long' })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Botones de acción */}
        <Box sx={{ display: 'flex', gap: 1, ml: 'auto', alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefrescar}
            disabled={cargandoMovimientos || cargandoPendientes || cargandoExportaciones}
          >
            Refrescar
          </Button>

          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setAbrirImportador(true)}
            color="primary"
          >
            Importar Excel
          </Button>

          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleAbrirModalExportacion}
            disabled={exportando}
            color="success"
          >
            {exportando ? 'Exportando...' : 'Exportar'}
          </Button>
        </Box>
      </Box>

      {/* Errores generales */}
      {errorMovimientos && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMovimientos('')}>
          {errorMovimientos}
        </Alert>
      )}

      {/* Badge de pendientes */}
      <BadgePendientes
        pendientes={pendientes}
        loading={cargandoPendientes}
        error={errorPendientes}
      />

      <Divider sx={{ my: 3 }} />

      {/* Grid de contenido */}
      <Grid container spacing={2}>
        {/* Columna izquierda: Movimientos */}
        <Grid item xs={12} md={6}>
          <MovimientosTab
            movimientos={movimientos}
            loading={cargandoMovimientos}
            onMovimientoSeleccionado={handleMovimientoSeleccionado}
            movimientoSeleccionado={movimientoSeleccionado}
            cuentas={cuentas}
            rutEmpresa={rutEmpresa}
            anio={anio}
            mes={mes}
          />
        </Grid>

        {/* Columna derecha: Formulario de edición */}
        <Grid item xs={12} md={6}>
          {movimientoSeleccionado ? (
            <FormularioMovimiento
              movimiento={movimientoSeleccionado}
              cuentas={cuentas}
              centros={centros}
              clasificadores={clasificadores}
              entidades={entidades}
              loading={guardandoMovimiento || cargandoCatalogos}
              error={''}
              onGuardar={handleGuardarMovimiento}
              onCancelar={handleCancelarEdicion}
            />
          ) : (
            <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography color="text.secondary">
                Selecciona un movimiento para editar
              </Typography>
            </Box>
          )}
        </Grid>

        {/* Historial de Exportaciones: ancho completo */}
        <Grid item xs={12}>
          <HistorialExportaciones
            exportaciones={exportaciones}
            loading={cargandoExportaciones}
            error={errorExportaciones}
            rutEmpresa={rutEmpresa}
          />
        </Grid>

        {/* Historial de Importaciones: ancho completo */}
        <Grid item xs={12}>
          <HistorialImportacionesTesoreria
            rutEmpresa={rutEmpresa}
            anio={anio}
            mes={mes}
            onImportacionEliminada={() => cargarMovimientos(rutEmpresa, anio, mes)}
          />
        </Grid>
      </Grid>

      {/* Modal de importación */}
      <ImportadorExcel
        open={abrirImportador}
        onClose={() => setAbrirImportador(false)}
        rutEmpresa={rutEmpresa}
        dvEmpresa={empresa.dv || empresa.dv_empresa}
        onImportExitoso={handleImportExitoso}
        anioDefault={anio}
        mesDefault={mes}
      />

      {/* Modal de confirmación de exportación */}
      <Dialog open={abrirModalExportacion} onClose={handleCerrarModalExportacion} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmar Exportación</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography>
              Se exportarán <strong>{cantidadParaExportar}</strong> movimiento{cantidadParaExportar !== 1 ? 's' : ''}
            </Typography>

            <TextField
              label="Folio Inicial"
              type="number"
              value={folioExportacion}
              onChange={(e) => setFolioExportacion(e.target.value)}
              placeholder="Dejar vacío para usar automático"
              fullWidth
              size="small"
              disabled={exportando}
              inputProps={{ min: '1' }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <input
                type="checkbox"
                id="forzarExportacionModal"
                checked={forzarExportacion}
                onChange={(e) => {
                  setForzarExportacion(e.target.checked);
                  // Recalcular cantidad
                  if (pendientes) {
                    const completadosNoExportados = pendientes.completados || 0;
                    const exportados = movimientos.filter(m => m.estado === 'EXPORTADO').length;
                    const cantidad = e.target.checked ? completadosNoExportados + exportados : completadosNoExportados;
                    setCantidadParaExportar(cantidad);
                  }
                }}
                disabled={exportando}
              />
              <label htmlFor="forzarExportacionModal" style={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                Incluir movimientos ya exportados (forzar)
              </label>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCerrarModalExportacion} disabled={exportando}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmarExportacion}
            variant="contained"
            color="success"
            disabled={exportando || cantidadParaExportar === 0}
          >
            {exportando ? 'Exportando...' : 'Confirmar Exportación'}
          </Button>
        </DialogActions>
      </Dialog>

    </CardContainer>
  );
};

export default TesoreríaTab;
